import { FC, useMemo, useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import GridOnIcon from "@mui/icons-material/GridOn";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { ScoringProfile, ScoringType } from "@/lib/scoring/types";
import { ScoreChip, getChipColor, getProfileColor } from "./ScoreChip";
import {
  getCriterionKey,
  FIT_CRITERION_KEYS,
  INTENT_CRITERION_KEYS,
} from "@/lib/scoring/criterionRegistry";
import { useHumanReadableBandRows } from "./useHumanReadableBandRows";

// ── Sub-component: resolved band details per criterion ────────────────────

const CriterionBandDetails: FC<{
  criterion: ScoringType;
  scores: Record<string, number>;
}> = ({ criterion, scores }) => {
  const { bandRows, contextHeader, loading } =
    useHumanReadableBandRows(criterion);
  const { name } = criterion;

  if (loading) {
    return (
      <Box sx={{ pl: 2, pr: 2, py: 1 }}>
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mt: 0.5 }} />
      </Box>
    );
  }

  return (
    <>
      {contextHeader && (
        <Box
          sx={{
            pl: 2,
            pr: 2,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {contextHeader.split(":")[0]}:
          </Typography>
          <Typography variant="body2">
            {contextHeader.split(":").slice(1).join(":").trim()}
          </Typography>
        </Box>
      )}
      {bandRows.map((band, idx) => {
        const matchKey = `${name} Match ${idx}`;
        const bandMatched = (scores[matchKey] ?? 0) !== 0;
        return (
          <Box key={idx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pl: 4,
                pr: 2,
                py: 0.75,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: bandMatched
                  ? "rgba(76, 175, 80, 0.04)"
                  : "transparent",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {bandMatched && (
                  <CheckIcon sx={{ fontSize: 16, color: "info.main" }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: bandMatched ? 600 : 400,
                    pl: bandMatched ? 0 : 2.75,
                  }}
                >
                  {band.summary}
                </Typography>
              </Box>
              <Typography variant="body2">{band.score}</Typography>
            </Box>
            {band.subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", pl: 4, pr: 2, py: 0.25 }}
              >
                {band.subtitle}
              </Typography>
            )}
          </Box>
        );
      })}
    </>
  );
};

// ── Sub-component: one profile accordion section ──────────────────────────

const ProfileSection: FC<{
  profile: ScoringProfile;
  profileOrder: number;
  scores: Record<string, number>;
  scoreType: "fit" | "intent";
  expanded: boolean;
  onToggle: () => void;
}> = ({ profile, profileOrder, scores, scoreType, expanded, onToggle }) => {
  const normalizedKey =
    scoreType === "intent" ? "Intent Score Normalized" : "Fit Score Normalized";
  const normalizedScore = scores[normalizedKey] ?? null;
  const chipColor = getChipColor(normalizedScore, profileOrder);
  const profileColor = getProfileColor(profileOrder);
  const scoreLabel = scoreType === "fit" ? "Fit Score" : "Intent Score";

  const targetKeys =
    scoreType === "fit" ? FIT_CRITERION_KEYS : INTENT_CRITERION_KEYS;

  const criteriaRows = useMemo(
    () =>
      profile.parameters.criteria
        .filter((c) => {
          const key = getCriterionKey(c);
          return key !== null && (targetKeys as string[]).includes(key);
        })
        .map((c) => {
          const { name, weight: criterionWeight } = c;
          const key = getCriterionKey(c)!;
          const weight = criterionWeight ?? 100;
          const points = scores[name] ?? 0;
          const matched = points > 0;
          const hasBands = ((c[key] as unknown[]) ?? []).length > 0;
          return { name, points, weight, matched, hasBands, criterion: c };
        }),
    [profile, scores, targetKeys]
  );

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const toggleRow = (name: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <Box>
      {/* Profile header — clickable accordion */}
      <Box
        onClick={onToggle}
        data-testid="score-drawer-profile-header"
        sx={{
          px: 2,
          py: 1.5,
          cursor: "pointer",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span data-testid="score-drawer-score-chip">
              <ScoreChip score={normalizedScore} color={chipColor} />
            </span>
            <Chip
              label={profile.name}
              size="small"
              color={profileColor}
              data-testid="score-drawer-profile-name"
              sx={{ borderRadius: 999, fontWeight: 700, fontSize: 11 }}
            />
          </Box>
          {expanded ? (
            <KeyboardArrowUpIcon sx={{ color: "text.secondary" }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ color: "text.secondary" }} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {scoreLabel}{" "}
          <Typography component="span" variant="caption" color="text.disabled">
            normalized
          </Typography>
        </Typography>
      </Box>

      {/* Expanded content */}
      {expanded && (
        <>
          <Divider />

          {/* Column header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 2,
              py: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Profile Scores
            </Typography>
            <Typography variant="body2">
              <Typography component="span" variant="body2" color="success.main">
                Actual Points
              </Typography>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
              >
                {" "}
                | Weight Given
              </Typography>
            </Typography>
          </Box>

          <Divider />

          {/* Criteria rows */}
          {criteriaRows.map((row) => {
            const isRowExpanded = expandedRows.has(row.name);
            return (
              <Box key={row.name}>
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row.hasBands) toggleRow(row.name);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    bgcolor: row.matched
                      ? "rgba(76, 175, 80, 0.04)"
                      : "transparent",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    cursor: row.hasBands ? "pointer" : "default",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: row.matched ? 600 : 400 }}
                  >
                    {row.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="body2">
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: row.matched ? "success.main" : "text.primary",
                          fontWeight: row.matched ? 700 : 400,
                        }}
                      >
                        {row.points}
                      </Typography>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        {" "}
                        | {row.weight}
                      </Typography>
                    </Typography>
                    {row.hasBands &&
                      (isRowExpanded ? (
                        <KeyboardArrowUpIcon
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          sx={{ fontSize: 18, color: "text.secondary" }}
                        />
                      ))}
                  </Box>
                </Box>

                {isRowExpanded && (
                  <CriterionBandDetails
                    criterion={row.criterion}
                    scores={scores}
                  />
                )}
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
};

// ── Main drawer component ─────────────────────────────────────────────────

interface ScoreDrawerProps {
  open: boolean;
  onClose: () => void;
  companyName: string;
  /** Raw scores record per profile — keyed by profile ID */
  scoresMap: Record<number, Record<string, number>>;
  activeProfiles: ScoringProfile[];
  /** The profile ID that was clicked (auto-expanded) */
  clickedProfileId: number | null;
  scoreType: "fit" | "intent";
  onChangeScoring?: () => void;
}

export const ScoreDrawer: FC<ScoreDrawerProps> = ({
  open,
  onClose,
  companyName,
  scoresMap,
  activeProfiles,
  clickedProfileId,
  scoreType,
  onChangeScoring,
}) => {
  const [expandedProfileIds, setExpandedProfileIds] = useState<Set<number>>(
    new Set()
  );

  // Auto-expand clicked profile when it changes
  useEffect(() => {
    if (clickedProfileId != null) {
      setExpandedProfileIds(new Set([clickedProfileId]));
    }
  }, [clickedProfileId]);

  const toggleProfile = (profileId: number) => {
    setExpandedProfileIds((prev) => {
      const next = new Set(prev);
      if (next.has(profileId)) next.delete(profileId);
      else next.add(profileId);
      return next;
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: { width: 400, p: 0, display: "flex", flexDirection: "column" },
        },
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, letterSpacing: 1 }}
        >
          COMPANY SCORE
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close score breakdown"
          data-testid="score-drawer-close-btn"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Company name */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: "grey.50",
        }}
      >
        <GridOnIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500 }}
          data-testid="score-drawer-company-name"
        >
          {companyName}
        </Typography>
      </Box>

      <Divider />

      {/* Profile sections */}
      {activeProfiles.map((profile, idx) => (
        <ProfileSection
          key={profile.id}
          profile={profile}
          profileOrder={idx + 1}
          scores={scoresMap[profile.id] ?? {}}
          scoreType={scoreType}
          expanded={expandedProfileIds.has(profile.id)}
          onToggle={() => toggleProfile(profile.id)}
        />
      ))}

      {/* Footer */}
      <Box
        sx={{
          mt: 2,
          p: 3,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {onChangeScoring && (
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              onClose();
              onChangeScoring();
            }}
          >
            CHANGE SCORING
          </Button>
        )}
      </Box>
    </Drawer>
  );
};
